import React, { useEffect, useState, useRef } from 'react';

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 6;

export default function ImageViewer({ src, images, alt = 'image', title, onClose }) {
  const list = images && images.length > 0 ? images : (src ? [src] : []);
  const multi = list.length > 1;

  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  const reset = () => { setZoom(1); setRot(0); setPan({ x: 0, y: 0 }); };

  useEffect(() => { reset(); }, [idx]);
  useEffect(() => { setIdx(0); reset(); }, [src, images]);

  const next = () => setIdx(i => (i + 1) % list.length);
  const prev = () => setIdx(i => (i - 1 + list.length) % list.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === '+' || e.key === '=') setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP));
      else if (e.key === '-') setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP));
      else if (e.key === '0') reset();
      else if (e.key === 'r' || e.key === 'R') setRot(r => (r + 90) % 360);
      else if (multi && e.key === 'ArrowRight') next();
      else if (multi && e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, multi, list.length]);

  const onWheel = (e) => {
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1 : -1;
    setZoom(z => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z + dir * ZOOM_STEP)));
  };

  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.panY + (e.clientY - dragRef.current.startY),
    });
  };
  const onMouseUp = () => { dragRef.current = null; };

  if (list.length === 0) return null;
  const current = list[idx];

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.toolbar} onClick={e => e.stopPropagation()}>
        {title && (
          <span style={s.title}>
            {title}{multi && <span style={s.counter}> · {idx + 1}/{list.length}</span>}
          </span>
        )}
        <button style={s.btn} onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP))} title="Zoom out (−)">
          <span className="material-symbols-outlined">remove</span>
        </button>
        <span style={s.zoomLabel}>{Math.round(zoom * 100)}%</span>
        <button style={s.btn} onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP))} title="Zoom in (+)">
          <span className="material-symbols-outlined">add</span>
        </button>
        <button style={s.btn} onClick={reset} title="Reset (0)">
          <span className="material-symbols-outlined">fit_screen</span>
        </button>
        <button style={s.btn} onClick={() => setRot(r => (r + 90) % 360)} title="Rotate (R)">
          <span className="material-symbols-outlined">rotate_right</span>
        </button>
        <button style={s.btn} onClick={onClose} title="Close (Esc)">
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div
        style={s.imgWrap}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onClick={e => e.stopPropagation()}
      >
        {multi && (
          <button style={{ ...s.navBtn, left: 16 }} onClick={prev} title="Previous (←)">
            <span className="material-symbols-outlined" style={{ fontSize: 28 }}>chevron_left</span>
          </button>
        )}
        <img
          src={current}
          alt={alt}
          draggable={false}
          style={{
            ...s.img,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rot}deg)`,
            cursor: zoom > 1 ? (dragRef.current ? 'grabbing' : 'grab') : 'default',
          }}
        />
        {multi && (
          <button style={{ ...s.navBtn, right: 16 }} onClick={next} title="Next (→)">
            <span className="material-symbols-outlined" style={{ fontSize: 28 }}>chevron_right</span>
          </button>
        )}
      </div>

      {multi && (
        <div style={s.thumbStrip} onClick={e => e.stopPropagation()}>
          {list.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`thumb ${i + 1}`}
              onClick={() => setIdx(i)}
              style={{
                ...s.thumb,
                borderColor: i === idx ? 'var(--accent)' : 'rgba(255,255,255,0.15)',
                opacity: i === idx ? 1 : 0.6,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(15, 23, 42, 0.85)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    zIndex: 300,
    display: 'flex', flexDirection: 'column',
  },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '12px 16px',
    background: 'rgba(15, 23, 42, 0.85)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },
  title: {
    color: '#e5e7eb', fontSize: 13, fontWeight: 600,
    marginRight: 'auto',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    maxWidth: '40vw',
  },
  counter: {
    fontWeight: 500, color: '#9ca3af',
    fontVariantNumeric: 'tabular-nums',
  },
  btn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32,
    background: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    color: '#e5e7eb',
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'background 110ms, border-color 110ms',
  },
  zoomLabel: {
    color: '#e5e7eb', fontSize: 12, fontWeight: 600,
    minWidth: 44, textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
  },
  imgWrap: {
    flex: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden',
    userSelect: 'none',
    position: 'relative',
  },
  img: {
    maxWidth: '90vw',
    maxHeight: '80vh',
    objectFit: 'contain',
    transition: 'transform 80ms ease-out',
    willChange: 'transform',
  },
  navBtn: {
    position: 'absolute',
    top: '50%', transform: 'translateY(-50%)',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 44, height: 44,
    background: 'rgba(15, 23, 42, 0.7)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    color: '#e5e7eb',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'background 110ms',
    zIndex: 1,
  },
  thumbStrip: {
    display: 'flex',
    gap: 8,
    padding: '12px 16px',
    background: 'rgba(15, 23, 42, 0.85)',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    overflowX: 'auto',
    justifyContent: 'center',
  },
  thumb: {
    width: 64, height: 64, objectFit: 'cover',
    borderRadius: 6,
    border: '2px solid',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'opacity 110ms, border-color 110ms',
  },
};
